import { Box, BoxProps, Stack, Typography, useTheme } from '@mui/material';
import Decimal from 'decimal.js';
import { isEmpty } from 'lodash';
import { ReactNode } from 'react';

import { Card } from '@/components/layout/Card/Card';
import { ClientPresentationDesignerV2_EntityFragment } from '@/modules/presentation/clientPresentation/graphql/ClientPresentationDesignerV2.generated';
import { PresentationSlide } from '@/modules/presentation/PresentationSlide';
import { PresentationSlideProps } from '@/modules/presentation/PresentationSlide/PresentationSlide';
import { SLIDE_HERO_CONTENT_TOP_OFFSET_SPACING } from '@/modules/slideEditor/shared/slideContentSpacing';
import { COLORS } from '@/styles/tokens/colors';
import { formatCurrency } from '@/utils/formatting/currency';

import {
  EntityDataPoints,
  EntityDetailsLists,
  EntityHeader,
} from './EntityCoverSlide.components';
import {
  getEntityCoverSlideData,
  getEntityCoverSlideSubheading,
} from './EntityCoverSlide.utils';

/** Wraps a cover-slide section with an overlay (e.g. an edit-mode hover toolbar). */
type SectionRenderer = (section: ReactNode) => ReactNode;

const renderSection = (
  section: ReactNode,
  renderer: SectionRenderer | undefined
): ReactNode => (renderer ? renderer(section) : section);

export interface EntityCoverSlideProps {
  entity: ClientPresentationDesignerV2_EntityFragment;
  SlideWrapper?: React.ComponentType<BoxProps>;
  id: string;
  footer?: React.ReactNode;
  Slide?: React.ComponentType<PresentationSlideProps>;
  /**
   * Wraps the title/icons section — used in the presentation designer to add
   * the edit-mode hover toolbar. Omitted in print/preview, where the section
   * renders plainly.
   */
  renderHeaderSection?: SectionRenderer;
  /**
   * Wraps the gray details block, only rendered when there are details to
   * show. Used in the designer to add the edit-mode hover toolbar.
   */
  renderDetailsSection?: SectionRenderer;
}

export function EntityCoverSlide({
  entity,
  SlideWrapper = Box,
  id,
  footer,
  Slide = PresentationSlide.Slide,
  renderHeaderSection,
  renderDetailsSection,
}: EntityCoverSlideProps) {
  const {
    title,
    dataPoints,
    detailsLists,
    detailsListsVariant,
    totalDeathBenefit,
  } = getEntityCoverSlideData(entity);
  const subheading = getEntityCoverSlideSubheading(entity);

  // The header hover region spans the title/icons and the data points beneath
  // it — they edit the same entity, so they're one hover surface. The inner
  // spacing matches the slide's `spacing={6}` so grouping them doesn't change
  // the layout.
  const headerSection = (
    <Stack spacing={6}>
      <Stack direction="row" justifyContent="space-between">
        <EntityHeader title={title} subheading={subheading} />
        <TotalDeathBenefit totalDeathBenefit={totalDeathBenefit} />
      </Stack>
      <EntityDataPoints dataPoints={dataPoints} />
    </Stack>
  );

  const detailsSection = !isEmpty(detailsLists) ? (
    <EntityDetailsLists lists={detailsLists} variant={detailsListsVariant} />
  ) : null;

  return (
    <SlideWrapper>
      <Slide
        id={id}
        footer={footer ?? <PresentationSlide.Footer />}
        readyToScale
      >
        <Stack
          spacing={6}
          sx={{
            pt: SLIDE_HERO_CONTENT_TOP_OFFSET_SPACING,
            px: '125px',
          }}
        >
          {renderSection(headerSection, renderHeaderSection)}
          {detailsSection &&
            renderSection(detailsSection, renderDetailsSection)}
        </Stack>
      </Slide>
    </SlideWrapper>
  );
}

interface TotalDeathBenefitProps {
  totalDeathBenefit: Decimal | undefined;
}

function TotalDeathBenefit({ totalDeathBenefit }: TotalDeathBenefitProps) {
  const theme = useTheme();
  if (!totalDeathBenefit || totalDeathBenefit.lessThanOrEqualTo(0)) return null;

  return (
    <Card
      variant="outlined"
      sx={{
        // Hug the content: without this the card stretches to the (taller)
        // header's height in the row, leaving dead space below the amount.
        alignSelf: 'flex-start',
        px: '14px',
        py: '10px',
      }}
    >
      <Stack>
        <Typography variant="title-xs" color={COLORS.TEXT.SECONDARY}>
          Total death benefit
        </Typography>
        <Typography
          variant="title-xl"
          component="span"
          color={theme.palette.primary.main}
        >
          {formatCurrency(totalDeathBenefit, { notation: 'compact' })}
        </Typography>
      </Stack>
    </Card>
  );
}
